import type { ReactNode } from 'react';
import { AliancaHomeCard } from '@/components/alianca/AliancaHomeCard';
import { ConnectorAvailPills } from '@/components/stations/ConnectorAvailPills';
import { TariffPrice } from '@/components/stations/TariffPrice';
import { HomeStationSheetExpanded } from './HomeStationSheetExpanded';
import { isStationOffline, peakKw, stationTariff } from '@/lib/stationStats';
import { formatDistance } from '@/lib/geo';
import type { StationDetailOut } from '@/api/stations';

export type SheetState =
  | { mode: 'peek' }
  | { mode: 'selected'; stationId: string };

interface Props {
  sheetState: SheetState;
  nearestStation: StationDetailOut | null;
  nearestDistanceKm: number | null;
  selectedStation: StationDetailOut | null;
  selectedDistanceKm: number | null;
  /** Alternativa quando a mais próxima está fora do ar (artboard "Home ·
   * peek · fora do ar"): próxima estação operacional, de preferência com
   * conector livre. `null` = não há alternativa (ou a mais próxima está ok). */
  fallbackStation: StationDetailOut | null;
  fallbackDistanceKm: number | null;
  onExpand: (stationId: string) => void;
  onCollapse: () => void;
  showAliancaCaptacao: boolean;
  /** Substitui o peek da "mais próxima" quando a posição do usuário não é
   * conhecida — sem ela o 1º da lista é só a ordem do backend, e destacá-lo
   * levava cliente a iniciar recarga no eletroposto errado (2026-10-08). */
  peekOverride?: ReactNode;
}

/** Sheet inferior da Home map-first — 2 estados por toque (peek ⇄ expandida),
 * sem gesto de arrastar contínuo (decisão Saulo 2026-07-05). */
export function HomeStationSheet({
  sheetState,
  nearestStation,
  nearestDistanceKm,
  selectedStation,
  selectedDistanceKm,
  fallbackStation,
  fallbackDistanceKm,
  onExpand,
  onCollapse,
  showAliancaCaptacao,
  peekOverride
}: Props) {
  const expanded = sheetState.mode === 'selected' && selectedStation !== null;
  const nearestTariff = nearestStation ? stationTariff(nearestStation) : null;
  const nearestOffline = nearestStation !== null && isStationOffline(nearestStation);

  return (
    <div
      className="absolute inset-x-0 bottom-0 z-10 flex max-h-[70dvh] flex-col gap-3 overflow-hidden rounded-t-[26px] bg-white px-5 pb-4 pt-2.5"
      style={{ boxShadow: '0 -10px 34px rgba(0,27,116,0.14)' }}
    >
      <div className="mx-auto h-[4.5px] w-10 flex-shrink-0 rounded-full bg-bow-royal/15" />

      {expanded ? (
        <HomeStationSheetExpanded
          station={selectedStation}
          distanceKm={selectedDistanceKm}
          onClose={onCollapse}
        />
      ) : peekOverride ? (
        peekOverride
      ) : nearestStation ? (
        <>
          <button
            type="button"
            onClick={() => onExpand(nearestStation.id)}
            className="flex items-center gap-3 rounded-[18px] bg-bow-cream-2/60 p-3 text-left"
          >
            {nearestStation.photo_url ? (
              <img
                src={nearestStation.photo_url}
                alt=""
                className="h-12 w-12 flex-shrink-0 rounded-2xl object-cover"
                style={nearestOffline ? { filter: 'grayscale(1)', opacity: 0.55 } : undefined}
              />
            ) : (
              <div className="h-12 w-12 flex-shrink-0 rounded-2xl bg-bow-royal/5" />
            )}
            <div className="min-w-0 flex-1">
              <div
                className={`truncate leading-tight ${nearestOffline ? 'text-bow-royal/60' : ''}`}
                style={{
                  fontFamily: 'Chillax, system-ui, sans-serif',
                  fontWeight: 600,
                  fontSize: 15.5,
                  letterSpacing: '-0.02em'
                }}
              >
                {nearestStation.name}
              </div>
              {nearestOffline ? (
                <OfflinePeekMeta distanceKm={nearestDistanceKm} />
              ) : (
                <PeekMeta station={nearestStation} distanceKm={nearestDistanceKm} />
              )}
            </div>
            {/* Bloco de preço à direita (Tarifa no Sheet Map-First.html ·
                Variação B): peso decisório no peek sem custar altura. Fora do
                ar não tem preço — não há recarga pra decidir. */}
            {nearestTariff && !nearestOffline && (
              <TariffPrice tariff={nearestTariff} className="flex-shrink-0 self-center" />
            )}
          </button>
          {nearestOffline ? (
            fallbackStation && (
            <button
              type="button"
              onClick={() => onExpand(fallbackStation.id)}
              className="flex items-center gap-2.5 rounded-2xl px-3 py-2.5 text-left"
              style={{
                background: 'rgba(0,192,114,0.08)',
                border: '1px solid rgba(0,192,114,0.16)'
              }}
            >
              <span className="h-2 w-2 flex-shrink-0 rounded-full bg-bow-green" />
              <span className="min-w-0 flex-1 text-[12px] font-medium tracking-[-0.01em]">
                Mais próxima disponível:{' '}
                <span className="font-semibold">{fallbackStation.name}</span>
                {fallbackDistanceKm !== null && <> · {formatDistance(fallbackDistanceKm)}</>}
              </span>
              <span
                className="inline-flex flex-shrink-0 items-center gap-0.5 text-[12px] font-semibold"
                style={{ color: '#008A4F' }}
              >
                Ver <ChevronRight />
              </span>
            </button>
            )
          ) : (
            showAliancaCaptacao && <AliancaHomeCard />
          )}
        </>
      ) : (
        <p className="px-1 pb-2 text-sm text-bow-royal/65">
          Nenhum eletroposto cadastrado.
        </p>
      )}
    </div>
  );
}

function PeekMeta({
  station,
  distanceKm
}: {
  station: StationDetailOut;
  distanceKm: number | null;
}) {
  const peak = Math.round(peakKw(station));
  return (
    <div className="mt-0.5 flex flex-col gap-1.5">
      <span className="text-[11.5px] font-semibold text-bow-royal/60">
        {distanceKm !== null && `${formatDistance(distanceKm)} · `}
        {peak > 0 ? `até ${peak} kW` : ''}
      </span>
      <ConnectorAvailPills station={station} />
    </div>
  );
}

/** Meta do peek quando a estação está fora do ar: sem kW nem pills de
 * conector — só distância + pílula "Fora do ar" (bolinha cinza com traço). */
function OfflinePeekMeta({ distanceKm }: { distanceKm: number | null }) {
  return (
    <div className="mt-0.5 flex flex-col gap-1.5">
      <span className="text-[11.5px] font-semibold text-bow-royal/50">
        {distanceKm !== null && `${formatDistance(distanceKm)} · `}
        recarga indisponível
      </span>
      <span
        className="inline-flex w-fit items-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-semibold"
        style={{ background: 'rgba(0,27,116,0.07)', color: 'rgba(0,27,116,0.5)' }}
      >
        <span className="flex h-3 w-3 items-center justify-center rounded-full bg-[#9AA0B4]">
          <svg width="6" height="6" viewBox="0 0 8 8" aria-hidden>
            <path d="M1.2 4h5.6" stroke="#FFFFFF" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </span>
        Fora do ar
      </span>
    </div>
  );
}

function ChevronRight() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}
